import type {
  AssignCarrierSubmitPayload,
  BookingRow,
  InviteCarrierLookupResult,
  useNotificationSnackbar,
} from "design-components";
import {
  applyDetailQuantitiesToBookingRow,
  formatBookingCarrierSelectLabel,
  formatBookingCarrierStatusLabel,
  isBookingCarrierBlacklisted,
  mapBookingDetailViewToDrawerModel,
  resolveAssignCarrierActionLabel,
  resolveAssignCarrierToolbarAction,
  useBookingCarriers,
  useBookingDetailDrawer,
} from "design-components";
import type { UpdateBookingData } from "api-client";
import { ApiErrorHandler } from "api-client";
import { useCallback, useMemo, useRef, useState } from "react";
import {
  BOOKING_DELETE_BLOCKED_STATUSES,
  buildBookingDeleteSuccessDescription,
  mapBookingDeletePreviewToDrawer,
} from "../../bookings/components/booking-delete.constants";
import type {
  BookingDeleteCarrierAssignment,
  BookingDeleteConfirmPayload,
  BookingDeleteSummary,
} from "../../bookings/components/booking-delete-drawer.types";
import { executeAssignCarrierSubmit } from "../../bookings/utils/execute-assign-carrier-submit.utils";
import { executeUnassignCarrier } from "../../bookings/utils/execute-unassign-carrier.utils";
import { bookingsService } from "../../../shared/services/bookings.service";
import type { AppDispatch } from "../../../shared/store/store";
import {
  deleteBooking,
  fetchBookingDeletePreview,
  markBookingDeleted,
} from "../../../shared/store/bookings";
import { fetchCarrierBookings } from "../../../shared/store/carrierDetail";

type ShowNotification = ReturnType<typeof useNotificationSnackbar>["showNotification"];

interface UseCarrierDetailBookingsActionsParams {
  dispatch: AppDispatch;
  bookingsRows: BookingRow[];
  showNotification: ShowNotification;
  /** Carrier detail page id — used for unassign close rules (FR-AV210-025). */
  pageCarrierId: string | null;
}

export function useCarrierDetailBookingsActions({
  dispatch,
  bookingsRows,
  showNotification,
  pageCarrierId,
}: UseCarrierDetailBookingsActionsParams) {
  const [editBookingOpen, setEditBookingOpen] = useState(false);
  const [editBookingId, setEditBookingId] = useState<number | null>(null);
  const [assignCarrierBooking, setAssignCarrierBooking] = useState<BookingRow | null>(null);
  const [bookingToDelete, setBookingToDelete] = useState<BookingDeleteSummary | null>(null);
  const [deleteCarrierAssignments, setDeleteCarrierAssignments] = useState<
    BookingDeleteCarrierAssignment[]
  >([]);
  const [deleteContainerQuantity, setDeleteContainerQuantity] = useState<number | null>(null);
  const [deleteIsMultipiece, setDeleteIsMultipiece] = useState(false);
  const [deleteCanNotifyCarrier, setDeleteCanNotifyCarrier] = useState(false);
  const [deleteInitialRecipients, setDeleteInitialRecipients] = useState<string[]>([]);
  const [deleteInitialMessageBody, setDeleteInitialMessageBody] = useState("");
  const [deletePreviewLoading, setDeletePreviewLoading] = useState(false);
  const [deleteSubmitError, setDeleteSubmitError] = useState<string | null>(null);

  const activeDeletePreviewBookingIdRef = useRef<number | null>(null);

  const fetchDetailView = useCallback(async (bookingId: number) => {
    const res = await bookingsService.getBookingDetailView(bookingId);
    return mapBookingDetailViewToDrawerModel(res.data);
  }, []);

  const handleInvalidBookingId = useCallback(
    (message: string) => {
      showNotification({
        variant: "error",
        title: "Cannot open details",
        description: message,
      });
    },
    [showNotification],
  );

  const {
    detailOpen,
    detailLoading,
    detailError,
    detailModel,
    detailContextRow,
    detailBookingId,
    handleBookingNumberClick,
    handleCloseDetail,
    refreshDetail,
  } = useBookingDetailDrawer({
    fetchDetailView,
    onInvalidBookingId: handleInvalidBookingId,
  });

  const {
    carriers: assignDrawerCarriersFromApi,
    loading: assignDrawerCarriersLoading,
  } = useBookingCarriers(bookingsService, {
    customerId: assignCarrierBooking?.customerId ?? null,
    enabled: assignCarrierBooking != null,
  });

  const carrierAssignSelectOptions = useMemo(
    () =>
      (assignDrawerCarriersFromApi?.carriers ?? []).map((c) => {
        const blacklisted = isBookingCarrierBlacklisted(c.status);
        return {
          value: String(c.carrierId),
          label: formatBookingCarrierSelectLabel(c.carrierName, c.dotNumber),
          plainLabel: c.carrierName,
          disabled: blacklisted,
          statusChipLabel: blacklisted ? formatBookingCarrierStatusLabel(c.status) : undefined,
        };
      }),
    [assignDrawerCarriersFromApi],
  );

  const refreshCarrierBookingsList = useCallback(() => {
    if (!pageCarrierId) return;
    dispatch(fetchCarrierBookings({ carrierId: pageCarrierId }));
  }, [dispatch, pageCarrierId]);

  const resetDeleteDrawerState = useCallback(() => {
    activeDeletePreviewBookingIdRef.current = null;
    setBookingToDelete(null);
    setDeleteCarrierAssignments([]);
    setDeleteContainerQuantity(null);
    setDeleteIsMultipiece(false);
    setDeleteCanNotifyCarrier(false);
    setDeleteInitialRecipients([]);
    setDeleteInitialMessageBody("");
    setDeletePreviewLoading(false);
    setDeleteSubmitError(null);
  }, []);

  const notifyInvalidBookingId = useCallback(
    (title: string) => {
      showNotification({
        variant: "error",
        title,
        description: "Invalid booking id.",
      });
    },
    [showNotification],
  );

  const handleCloseEditBooking = useCallback(() => {
    setEditBookingOpen(false);
    setEditBookingId(null);
  }, []);

  const handleEditBookingUpdated = useCallback(
    (data: UpdateBookingData, message: string) => {
      const bookingNumber = data.bookingNumber?.trim();
      const apiMessage = message.trim();
      let description = apiMessage;
      if (!description) {
        description = bookingNumber
          ? `Booking number - ${bookingNumber} has been updated successfully`
          : "Booking has been updated successfully";
      }
      showNotification({
        variant: "success",
        title: "Booking updated",
        description,
      });
      refreshCarrierBookingsList();
    },
    [refreshCarrierBookingsList, showNotification],
  );

  const handleDetailEdit = useCallback(() => {
    if (detailBookingId == null) return;
    handleCloseDetail();
    setEditBookingId(detailBookingId);
    setEditBookingOpen(true);
  }, [detailBookingId, handleCloseDetail]);

  const handleDetailReassign = useCallback(() => {
    if (!detailContextRow) return;
    if (!detailModel?.isMultipieceBooking) {
      handleCloseDetail();
    }
    const listRow = bookingsRows.find((r) => r.id === detailContextRow.id) ?? detailContextRow;
    setAssignCarrierBooking(applyDetailQuantitiesToBookingRow(listRow, detailModel));
  }, [bookingsRows, detailContextRow, detailModel, handleCloseDetail]);

  const closeDetailAfterPageCarrierUnassign = useCallback((): Promise<void> => {
    handleCloseDetail();
    return Promise.resolve();
  }, [handleCloseDetail]);

  const handleDetailUnassignCarrier = useCallback(
    async (assignmentId: number) => {
      const assignment = detailModel?.carrierAssignments.find(
        (row) => row.assignmentId === assignmentId,
      );
      const unassignedCarrierId = assignment?.carrierId;
      const isCurrentPageCarrier =
        pageCarrierId != null &&
        unassignedCarrierId != null &&
        String(unassignedCarrierId) === String(pageCarrierId);

      await executeUnassignCarrier({
        bookingId: detailBookingId,
        assignmentId,
        carrierName: assignment?.carrierName ?? "Carrier",
        bookingNumber: detailModel?.bookingNumber ?? "",
        showNotification,
        refreshDetail: isCurrentPageCarrier
          ? closeDetailAfterPageCarrierUnassign
          : refreshDetail,
        onListRefresh: refreshCarrierBookingsList,
      });
    },
    [
      closeDetailAfterPageCarrierUnassign,
      detailBookingId,
      detailModel,
      pageCarrierId,
      refreshCarrierBookingsList,
      refreshDetail,
      showNotification,
    ],
  );

  const openDeletePreviewForRow = useCallback(
    (row: BookingRow) => {
      if (BOOKING_DELETE_BLOCKED_STATUSES.includes(row.status)) {
        showNotification({
          variant: "warning",
          title: "Cannot delete",
          description: `Bookings with status "${row.status}" cannot be deleted.`,
        });
        return;
      }
      const bookingIdNum = parseInt(row.id, 10);
      if (!Number.isFinite(bookingIdNum)) {
        notifyInvalidBookingId("Cannot delete");
        return;
      }

      activeDeletePreviewBookingIdRef.current = bookingIdNum;
      setDeletePreviewLoading(true);
      setBookingToDelete({
        bookingId: row.id,
        bookingNumber: row.bookingNumber,
        arrivalAt: row.arrivalAt,
        locationLabel: row.locationLabel,
      });
      setDeleteCarrierAssignments([]);
      setDeleteContainerQuantity(null);
      setDeleteIsMultipiece(false);
      setDeleteCanNotifyCarrier(false);
      setDeleteInitialRecipients([]);
      setDeleteInitialMessageBody("");

      void (async () => {
        try {
          const res = await dispatch(fetchBookingDeletePreview(bookingIdNum)).unwrap();
          if (activeDeletePreviewBookingIdRef.current !== bookingIdNum) return;
          const mapped = mapBookingDeletePreviewToDrawer(res.data);
          setBookingToDelete(mapped.booking);
          setDeleteCarrierAssignments(mapped.carrierAssignments);
          setDeleteContainerQuantity(mapped.containerQuantity);
          setDeleteIsMultipiece(mapped.isMultipiece);
          setDeleteCanNotifyCarrier(mapped.canNotifyCarrier);
          setDeleteInitialRecipients(mapped.initialRecipients);
          setDeleteInitialMessageBody(mapped.initialMessageBody);
        } catch (e: unknown) {
          if (activeDeletePreviewBookingIdRef.current !== bookingIdNum) return;
          resetDeleteDrawerState();
          showNotification({
            variant: "error",
            title: "Cannot delete",
            description: ApiErrorHandler.extractAxiosError(e, "Failed to load delete preview."),
          });
        } finally {
          if (activeDeletePreviewBookingIdRef.current === bookingIdNum) {
            setDeletePreviewLoading(false);
          }
        }
      })();
    },
    [dispatch, notifyInvalidBookingId, resetDeleteDrawerState, showNotification],
  );

  const handleDetailDelete = useCallback(() => {
    if (!detailContextRow) return;
    const row = detailContextRow;
    handleCloseDetail();
    openDeletePreviewForRow(row);
  }, [detailContextRow, handleCloseDetail, openDeletePreviewForRow]);

  const handleConfirmDeleteBooking = useCallback(
    async (payload: BookingDeleteConfirmPayload) => {
      const bookingIdNum = Number(payload.bookingId);
      if (!Number.isFinite(bookingIdNum)) {
        setDeleteSubmitError("Invalid booking id.");
        notifyInvalidBookingId("Cannot delete");
        return;
      }
      setDeleteSubmitError(null);
      try {
        const res = await dispatch(
          deleteBooking({
            bookingId: bookingIdNum,
            recipientEmails: payload.recipients.join(","),
            emailMessage: payload.messageBody,
            ...(payload.reason !== undefined ? { reason: payload.reason } : {}),
          }),
        ).unwrap();
        const bookingNumber =
          res.data.bookingNumber?.trim() || bookingToDelete?.bookingNumber?.trim() || "";
        dispatch(markBookingDeleted(payload.bookingId));
        resetDeleteDrawerState();
        showNotification({
          variant: "success",
          title: "Booking deleted",
          description: buildBookingDeleteSuccessDescription(
            bookingNumber,
            res.message?.trim(),
            res.data.emailSent,
          ),
        });
        refreshCarrierBookingsList();
      } catch (e: unknown) {
        const message = ApiErrorHandler.extractAxiosError(e, "Failed to delete booking.");
        setDeleteSubmitError(message);
        showNotification({
          variant: "error",
          title: "Delete failed",
          description: message,
        });
      }
    },
    [
      bookingToDelete,
      dispatch,
      notifyInvalidBookingId,
      refreshCarrierBookingsList,
      resetDeleteDrawerState,
      showNotification,
    ],
  );

  const handleCloseDeleteDrawer = useCallback(() => {
    if (deletePreviewLoading) return;
    resetDeleteDrawerState();
  }, [deletePreviewLoading, resetDeleteDrawerState]);

  const handleCloseAssignCarrier = useCallback(() => {
    setAssignCarrierBooking(null);
  }, []);

  const handleDotLookup = useCallback(async (dot: string): Promise<InviteCarrierLookupResult> => {
    const res = await bookingsService.lookupCarrierByDot(dot);
    if (res.statusCode !== 200) {
      throw new Error(res.message?.trim() || "Could not look up carrier by DOT. Try again.");
    }
    const d = res.data;
    return {
      carrierName: d.carrierName,
      contactName: d.contactName,
      email: d.email,
      businessAddress: d.businessAddress,
      dotStatus: d.dotStatus,
      riskScore: d.riskScore,
      safetyScore: String(d.safetyScore),
    };
  }, []);

  const handleConfirmAssignCarrier = useCallback(
    async (payload: AssignCarrierSubmitPayload) => {
      const row = bookingsRows.find((r) => r.id === payload.bookingId) ?? assignCarrierBooking;
      if (!row) {
        throw new Error("Booking not found. Refresh the list and try again.");
      }
      await executeAssignCarrierSubmit({
        row,
        payload,
        showNotification,
        notificationTitles: {
          error: "Carrier assignment failed",
          success: "Carrier assigned",
          inviteSuccess: "Invite sent",
        },
        onSuccess: () => {
          setAssignCarrierBooking(null);
          refreshCarrierBookingsList();
          if (detailOpen && detailBookingId != null) {
            void refreshDetail();
          }
        },
      });
    },
    [
      assignCarrierBooking,
      bookingsRows,
      detailBookingId,
      detailOpen,
      refreshCarrierBookingsList,
      refreshDetail,
      showNotification,
    ],
  );

  const handleClearDeleteSubmitError = useCallback(() => {
    setDeleteSubmitError(null);
  }, []);

  const detailAssignToolbarAction = useMemo(() => {
    if (!detailContextRow) {
      return null;
    }
    return resolveAssignCarrierToolbarAction([detailContextRow.id], bookingsRows);
  }, [bookingsRows, detailContextRow]);

  const assignCarrierMenuLabel = useMemo(() => {
    if (!detailContextRow) {
      return null;
    }
    return resolveAssignCarrierActionLabel(detailContextRow.status, detailContextRow);
  }, [detailContextRow]);

  return {
    detailOpen,
    detailLoading,
    detailError,
    detailModel,
    detailContextRow,
    editBookingOpen,
    editBookingId,
    assignCarrierBooking,
    bookingToDelete,
    deleteCarrierAssignments,
    deleteContainerQuantity,
    deleteIsMultipiece,
    deleteCanNotifyCarrier,
    deleteInitialRecipients,
    deleteInitialMessageBody,
    deletePreviewLoading,
    deleteSubmitError,
    carrierAssignSelectOptions,
    assignDrawerCarriersLoading,
    handleBookingNumberClick,
    handleCloseDetail,
    handleCloseEditBooking,
    handleEditBookingUpdated,
    handleDetailEdit,
    handleDetailReassign,
    handleDetailUnassignCarrier,
    handleDetailDelete,
    handleConfirmDeleteBooking,
    handleCloseDeleteDrawer,
    handleCloseAssignCarrier,
    handleDotLookup,
    handleConfirmAssignCarrier,
    handleClearDeleteSubmitError,
    detailAssignToolbarAction,
    assignCarrierMenuLabel,
  };
}
